/**
 * Reconciliation Status Page
 *
 * Feature: 023-service-principal-registry
 * Task: T034 - Create ReconciliationStatusPage with logs table
 * Task: T043 - Add drifts panel
 * Task: T050 - Add SP deletion alert panel
 * Task: T057 - Add export status section
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Page displaying reconciliation status, active drifts, logs, and exports.
 * Conformed to Spec 197 design blueprint — presentation-only migration onto the
 * canonical Tailwind+token UI kit (no data/logic/state/route/behavior changes).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ArrowLeft, AlertTriangle, Upload, Loader2 } from 'lucide-react';
import { SpReconciliationLog, ReconciliationType, ReconciliationStatus } from '../types';
import {
  useReconciliationStatus,
  useReconciliationLogs,
  useActiveDrifts,
  useTriggerReconciliation,
  useFixDrift,
  useResolveDrift,
  useExportHistory,
  useTriggerExport,
} from '../hooks/useServicePrincipals';
import { ReconciliationCard } from '../components/ReconciliationCard';
import { DriftsList } from '../components/DriftsList';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
  Extended,
  QuickGlance,
  NotCollected,
} from '../../../components/ui';
import type { SpExportFile } from '../services/servicePrincipalApi';
import type { DataTableColumn } from '../../../components/ui';
import { servicePrincipalHelp } from '../../../data/help/servicePrincipalHelp';
import { formatEastern } from '@/utils/dateFormatter';

/**
 * Map a reconciliation status to a kit <Badge> variant.
 */
const statusVariant: Record<ReconciliationStatus, 'info' | 'success' | 'warning' | 'danger'> = {
  RUNNING: 'info',
  COMPLETED: 'success',
  PARTIAL: 'warning',
  FAILED: 'danger',
};

/**
 * Reconciliation type labels
 */
const typeLabels: Record<ReconciliationType, string> = {
  daily_permissions: 'Permissions Check',
  weekly_entra: 'Entra Verification',
  weekly_export: 'Export',
  manual: 'Manual',
};

/**
 * Format date for display
 */
function formatDateTime(date: Date | string | null): string {
  if (!date) return '—';
  return formatEastern(date);
}

/**
 * Format duration
 */
function formatDuration(start: Date | string, end: Date | string | null): string {
  if (!end) return '—';
  const durationMs = new Date(end).getTime() - new Date(start).getTime();
  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  return `${Math.round(durationMs / 60000)}m ${Math.round((durationMs % 60000) / 1000)}s`;
}

/**
 * Format file size
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Tabs for the page
 */
type TabId = 'overview' | 'drifts' | 'logs' | 'exports';

/**
 * Reconciliation Status Page Component
 */
export const ReconciliationStatusPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [fixingDriftId, setFixingDriftId] = useState<string | null>(null);
  const [resolvingDriftId, setResolvingDriftId] = useState<string | null>(null);

  // Queries
  const queryClient = useQueryClient();
  // Spec 298 FR-006 (rows 46-50): each read returns the route's body, {success, data, pagination?}
  // (servicePrincipalApi.ts); the page reads data and pagination. No body yet is pending or paused; a failed
  // read is said so; neither is a 0.
  const { data: statusData, isLoading: statusLoading, isError: statusError } = useReconciliationStatus();
  const { data: logsData, isLoading: logsLoading, isError: logsError } = useReconciliationLogs({ limit: 20 });
  const { data: driftsData, isLoading: driftsLoading, isError: driftsError } = useActiveDrifts();
  const { data: exportsData, isLoading: exportsLoading, isError: exportsError } = useExportHistory();
  const notRead = (failed: boolean, what: string) => (failed ? `${what} could not be read` : 'loading…');

  // The status route sends no lastRun: the last run is the newest of its recentRuns (newest first,
  // SpReconciliationLogRepository.getRecent ORDER BY started_at DESC). undefined = not read; null = none recorded.
  const lastRun = statusData ? (statusData.data.recentRuns[0] ?? null) : undefined;
  const exportFiles = exportsData?.data;
  const lastExportAt = exportFiles?.length
    ? exportFiles.reduce((newest, e) => (e.createdAt > newest ? e.createdAt : newest), exportFiles[0].createdAt)
    : null;

  // Mutations
  const triggerMutation = useTriggerReconciliation();
  const fixDriftMutation = useFixDrift();
  const resolveDriftMutation = useResolveDrift();
  const triggerExportMutation = useTriggerExport();

  /**
   * Handle trigger reconciliation
   */
  const handleTriggerRun = async (type: ReconciliationType) => {
    // Filter out 'manual' as it's not a valid trigger type
    if (type === 'manual') return;
    await triggerMutation.mutateAsync(type);
  };

  /**
   * Handle fix drift
   */
  const handleFixDrift = async (driftId: string) => {
    setFixingDriftId(driftId);
    try {
      await fixDriftMutation.mutateAsync(driftId);
    } finally {
      setFixingDriftId(null);
    }
  };

  /**
   * Handle resolve drift
   */
  const handleResolveDrift = async (driftId: string, resolution: string) => {
    setResolvingDriftId(driftId);
    try {
      await resolveDriftMutation.mutateAsync({ driftId, resolution });
    } finally {
      setResolvingDriftId(null);
    }
  };

  /**
   * Handle trigger export
   */
  const handleTriggerExport = async () => {
    await triggerExportMutation.mutateAsync();
  };

  // Get critical alerts (SP_DELETED drifts)
  const criticalDrifts = driftsData?.data.filter((d) => d.driftStatus === 'SP_DELETED') ?? [];

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    // No badge until the drifts are read (undefined), never a badge of 0.
    { id: 'drifts', label: 'Active Drifts', count: driftsData?.pagination.total },
    { id: 'logs', label: 'Run History' },
    { id: 'exports', label: 'Exports' },
  ];

  // ----------------------------------------------------------------------------
  // Table columns
  // ----------------------------------------------------------------------------

  const logColumns: DataTableColumn<SpReconciliationLog>[] = [
    {
      key: 'type',
      header: 'Type',
      width: '16%',
      render: (log) => typeLabels[log.reconciliationType],
    },
    {
      key: 'status',
      header: 'Status',
      width: '11%',
      render: (log) => <Badge variant={statusVariant[log.status]}>{log.status}</Badge>,
    },
    {
      key: 'started',
      header: 'Started',
      width: '15%',
      render: (log) => (
        <span className="text-[var(--color-text-secondary)]">{formatDateTime(log.startedAt)}</span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      width: '9%',
      render: (log) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatDuration(log.startedAt, log.completedAt)}
        </span>
      ),
    },
    { key: 'sps', header: 'SPs', width: '7%', align: 'right', render: (log) => log.spsChecked },
    {
      key: 'assignments',
      header: 'Assignments',
      width: '11%',
      align: 'right',
      render: (log) => log.assignmentsChecked,
    },
    {
      key: 'drifts',
      header: 'Drifts',
      width: '8%',
      align: 'right',
      render: (log) => (
        <span
          className={clsx(
            'font-medium',
            log.driftsDetected > 0
              ? 'text-[var(--color-status-warning)]'
              : 'text-[var(--color-text-secondary)]',
          )}
        >
          {log.driftsDetected}
        </span>
      ),
    },
    {
      key: 'autoFixed',
      header: 'Auto-Fixed',
      width: '11%',
      align: 'right',
      render: (log) => (
        <span
          className={clsx(
            'font-medium',
            log.driftsFixed > 0
              ? 'text-[var(--color-status-success)]'
              : 'text-[var(--color-text-secondary)]',
          )}
        >
          {log.driftsFixed}
        </span>
      ),
    },
    {
      key: 'manual',
      header: 'Manual Review',
      width: '12%',
      align: 'right',
      render: (log) => (
        <span
          className={clsx(
            'font-medium',
            log.driftsManual > 0
              ? 'text-[var(--color-status-warning)]'
              : 'text-[var(--color-text-secondary)]',
          )}
        >
          {log.driftsManual}
        </span>
      ),
    },
  ];

  const exportColumns: DataTableColumn<SpExportFile>[] = [
    {
      key: 'filename',
      header: 'Filename',
      width: '60%',
      render: (exp) => <span className="font-mono text-[var(--color-text-primary)]">{exp.filename}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '25%',
      render: (exp) => (
        <span className="text-[var(--color-text-secondary)]">{formatDateTime(exp.createdAt)}</span>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      width: '15%',
      align: 'right',
      render: (exp) => (
        <span className="text-[var(--color-text-secondary)]">{formatFileSize(exp.size)}</span>
      ),
    },
  ];

  return (
    <PageLayout
      title="Reconciliation Status"
      description="Monitor permission drift detection, auto-fixes, and scheduled exports"
      actions={<PageHelpPanel content={servicePrincipalHelp} buttonSize="sm" />}
      // Spec 285 / 226: status, run history, drifts and exports all sit under 'service-principals'.
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ['service-principals'] })}
    >
      {/* Breadcrumb */}
      <nav className="mb-4">
        <Link
          to="/service-principals"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Service Principals
        </Link>
      </nav>

      {/* Critical Alert Banner (SP_DELETED) */}
      {criticalDrifts.length > 0 && (
        <Card className="mb-6 border-l-4 border-l-[var(--color-status-danger)] bg-[var(--color-status-danger)]/5 p-4">
          <div className="flex gap-3">
            <AlertTriangle className="h-6 w-6 flex-shrink-0 text-[var(--color-status-danger)]" />
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-status-danger)]">
                CRITICAL: {criticalDrifts.length} Service Principal
                {criticalDrifts.length !== 1 ? 's' : ''} Deleted from Entra ID
              </h3>
              <div className="mt-2 text-sm text-[var(--color-text-secondary)]">
                <p>The following service principals have been deleted from Microsoft Entra ID:</p>
                <ul className="mt-1 list-inside list-disc">
                  {criticalDrifts.slice(0, 3).map((d) => (
                    <li key={d.id}>{d.servicePrincipalName}</li>
                  ))}
                  {criticalDrifts.length > 3 && <li>...and {criticalDrifts.length - 3} more</li>}
                </ul>
              </div>
              <div className="mt-3">
                <h4 className="text-sm font-semibold text-[var(--color-status-danger)]">
                  Recommended Recovery Steps:
                </h4>
                <ol className="mt-1 list-inside list-decimal text-sm text-[var(--color-text-secondary)]">
                  <li>Check if deletion was intentional</li>
                  <li>If unintentional, restore SP from Entra ID deleted items (within 30 days)</li>
                  <li>Update registry status once restored</li>
                  <li>Verify workspace permissions are intact</li>
                </ol>
              </div>
            </div>
          </div>
        </Card>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              '-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <Badge variant={tab.id === 'drifts' ? 'warning' : 'secondary'} size="xs">
                {tab.count}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Extended>
        <div className="space-y-6">
          {/* Status Card */}
          {statusLoading ? (
            <Card className="p-6">
              <LoadingState rows={4} />
            </Card>
          ) : (
            statusData && (
              <Card padding="none">
                {/* The status route reports no in-progress flag and no schedule: running = the newest recorded
                    run is RUNNING (the backend refuses a second run of a type with 409); the schedule is not sent. */}
                <ReconciliationCard
                  isRunning={lastRun?.status === 'RUNNING'}
                  lastRun={lastRun ?? null}
                  nextScheduledRun={null}
                  onTriggerRun={handleTriggerRun}
                  isTriggering={triggerMutation.isPending}
                />
              </Card>
            )
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Row 47: this read "Total Runs (Last 7 Days)" over a page of 20. The logs route counts the newest
                100 runs recorded, of every type (reconciliationService.getReconciliationLogs); no route offers a
                7-day count. */}
            <AppTooltip title="Reconciliation runs recorded, of every type. The run history counts the newest 100 at most.">
              <StatCard
                label="Runs Recorded"
                value={logsData ? (logsData.pagination.total >= 100 ? '100+' : logsData.pagination.total) : '—'}
                subtext={logsData ? undefined : notRead(logsError, 'run history')}
              />
            </AppTooltip>
            <StatCard
              label="Active Drifts"
              value={driftsData ? driftsData.pagination.total : '—'}
              subtext={driftsData ? undefined : notRead(driftsError, 'drifts')}
              valueClassName={
                !driftsData
                  ? undefined
                  : driftsData.pagination.total > 0
                    ? 'text-[var(--color-status-warning)]'
                    : 'text-[var(--color-status-success)]'
              }
            />
            <StatCard
              label="Auto-Fixed (Last Run)"
              value={lastRun === undefined ? '—' : lastRun === null ? 'no run recorded' : lastRun.driftsFixed}
              subtext={lastRun === undefined ? notRead(statusError, 'status') : undefined}
              valueClassName={lastRun ? 'text-[var(--color-status-success)]' : undefined}
            />
            <StatCard
              label="Exports Available"
              value={exportFiles ? (exportFiles.length > 0 ? exportFiles.length : 'no exports') : '—'}
              subtext={exportFiles ? undefined : notRead(exportsError, 'exports')}
            />
          </div>
        </div>
        </Extended>
      )}

      {activeTab === 'drifts' && (
        <Extended>
        <Card padding="none">
          {driftsError && !driftsData ? (
            <ErrorState title="Could not read the active drifts" className="m-4" />
          ) : (
            <DriftsList
              drifts={driftsData?.data ?? []}
              isLoading={driftsLoading || !driftsData}
              onFix={handleFixDrift}
              onResolve={handleResolveDrift}
              fixingId={fixingDriftId}
              resolvingId={resolvingDriftId}
            />
          )}
        </Card>
        </Extended>
      )}

      {activeTab === 'logs' && (
        <Extended>
        <Card padding="none">
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              Reconciliation Run History
            </h2>
          </div>

          {logsError && !logsData ? (
            <ErrorState title="Could not read the run history" className="m-4" />
          ) : logsLoading || !logsData ? (
            <div className="p-4">
              <LoadingState rows={6} />
            </div>
          ) : (
            <DataTable
              columns={logColumns}
              rows={logsData.data}
              rowKey={(log) => log.runId}
              minWidth="64rem"
              emptyMessage="No reconciliation runs yet"
            />
          )}
        </Card>
        </Extended>
      )}

      {activeTab === 'exports' && (
        <Extended>
        <div className="space-y-6">
          {/* Export Actions */}
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Weekly Exports</h2>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  SP registry exports for disaster recovery and compliance
                </p>
              </div>
              <AppTooltip title="Trigger an immediate SP registry export for DR and compliance">
                <Button
                  variant="primary"
                  onClick={handleTriggerExport}
                  disabled={triggerExportMutation.isPending}
                  leftIcon={
                    triggerExportMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )
                  }
                >
                  {triggerExportMutation.isPending ? 'Exporting...' : 'Export Now'}
                </Button>
              </AppTooltip>
            </div>

            {exportFiles && (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-[var(--color-bg-subtle)] p-4">
                  <p className="text-sm text-[var(--color-text-secondary)]">Last Export</p>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {/* The exports route sends the files only: the last export is the newest file. */}
                    {lastExportAt ? formatDateTime(lastExportAt) : 'Never'}
                  </p>
                </div>
                <div className="rounded-lg bg-[var(--color-bg-subtle)] p-4">
                  <p className="text-sm text-[var(--color-text-secondary)]">Next Scheduled</p>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    <NotCollected reason="The exports route does not report the export schedule." />
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Export History */}
          <Card padding="none">
            <div className="border-b border-[var(--color-border)] px-4 py-3">
              <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Export History</h2>
              <p className="text-sm text-[var(--color-text-secondary)]">Exports are retained for 90 days</p>
            </div>

            {exportsError && !exportFiles ? (
              <ErrorState title="Could not read the export history" className="m-4" />
            ) : exportsLoading || !exportFiles ? (
              <div className="p-4">
                <LoadingState rows={4} />
              </div>
            ) : exportFiles.length > 0 ? (
              <DataTable
                columns={exportColumns}
                rows={exportFiles}
                rowKey={(exp) => exp.filename}
                minWidth="44rem"
                emptyMessage="No exports yet"
              />
            ) : (
              <EmptyState title="No exports yet" />
            )}
          </Card>
        </div>
        </Extended>
      )}
    </PageLayout>
  );
};

export default ReconciliationStatusPage;
